// Panel «Datos legales» de Administración · Empresa (handoff §2): razón
// social y RUT, con «Guardar cambios» —el único primario de la pantalla—
// deshabilitado hasta que algo cambie. El RUT se valida (dígito
// verificador) al guardar y viaja normalizado (./model.ts); un 400
// RUT_INVALID del servidor se muestra junto al campo igual que el rechazo
// local.
//
// Sin «Correo de respuesta»: organizations no tiene esa columna y esta
// ronda no migra, así que el campo se oculta (regla «hide unusable
// actions»), no se simula.
//
// El panel es dueño de su borrador y de «lo que se mostró al abrir»
// (initial): al guardar, ambos se reinician desde la respuesta del PATCH
// —así el formulario queda limpio aunque la fila no haya cambiado (p. ej.
// el mismo RUT escrito sin puntos)— y el foco se queda donde estaba, sin
// remontar el panel. Si el servidor trae otros valores (un refresh, otro
// administrador) se adoptan sólo cuando no hay un borrador en curso.

import { useEffect, useId, useState } from "react";
import type { MyOrganizationResponse } from "@bitacorapro/api-types";
import { Panel } from "../../../components/Panel";
import { ApiError } from "../../../lib/api/error";
import {
  RUT_INVALID_MESSAGE,
  datosLegalesDirty,
  datosLegalesDraftOf,
  datosLegalesPatch,
  type DatosLegalesDraft,
} from "./model";
import { useUpdateDatosLegales } from "./queries";

export type DatosLegalesPanelProps = {
  org: Pick<MyOrganizationResponse, "name" | "rut">;
  /** Mensaje del toast que muestra la pantalla al guardar. */
  onSaved: (message: string) => void;
};

type FieldError = { field: "name" | "rut"; message: string } | { field: "form"; message: string };

export function DatosLegalesPanel({ org, onSaved }: DatosLegalesPanelProps) {
  const [initial, setInitial] = useState<DatosLegalesDraft>(() => datosLegalesDraftOf(org));
  const [draft, setDraft] = useState<DatosLegalesDraft>(initial);
  const [error, setError] = useState<FieldError | null>(null);
  const update = useUpdateDatosLegales();
  const formId = useId();
  const nameId = useId();
  const rutId = useId();
  const rutHintId = useId();
  const errorId = useId();

  const dirty = datosLegalesDirty(initial, draft);
  const canSave = dirty && !update.isPending;

  // Valores nuevos del servidor sin borrador en curso: se adoptan. Con un
  // borrador a medias no se pisa lo que la persona está escribiendo.
  const { name: serverName, rut: serverRut } = org;
  useEffect(() => {
    const fromServer = datosLegalesDraftOf({ name: serverName, rut: serverRut });
    if (dirty || !datosLegalesDirty(initial, fromServer)) return;
    setInitial(fromServer);
    setDraft(fromServer);
  }, [serverName, serverRut, dirty, initial]);

  function set(patch: Partial<DatosLegalesDraft>) {
    setDraft((d) => ({ ...d, ...patch }));
    setError(null);
  }

  async function save() {
    const next = datosLegalesPatch(initial, draft);
    if (next.kind === "unchanged") return;
    if (next.kind === "invalid") {
      setError({ field: next.field, message: next.message });
      return;
    }
    setError(null);
    try {
      const updated = await update.mutateAsync(next.body);
      // Lo guardado manda: el RUT vuelve con puntos y el formulario queda
      // limpio aunque la fila no haya cambiado.
      const saved = datosLegalesDraftOf(updated);
      setInitial(saved);
      setDraft(saved);
      onSaved("Datos de la empresa guardados");
    } catch (err) {
      if (err instanceof ApiError && err.code === "RUT_INVALID") {
        setError({ field: "rut", message: RUT_INVALID_MESSAGE });
      } else {
        setError({ field: "form", message: "No pudimos guardar los datos. Intenta de nuevo." });
      }
    }
  }

  const nameError = error?.field === "name" ? error.message : null;
  const rutError = error?.field === "rut" ? error.message : null;
  const formError = error?.field === "form" ? error.message : null;

  return (
    <Panel
      title="Datos legales"
      actions={
        <button
          type="submit"
          form={formId}
          className="btn btn--primary btn--sm"
          title="Guardar la razón social y el RUT de la empresa"
          disabled={!canSave}
        >
          {update.isPending ? "Guardando…" : "Guardar cambios"}
        </button>
      }
    >
      <form
        id={formId}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {formError && (
          <p id={errorId} className="auth-error" role="alert">
            {formError}
          </p>
        )}
        <div className="field ag-field">
          <label className="field__label" htmlFor={nameId}>
            Razón social
          </label>
          <input
            id={nameId}
            className="input"
            value={draft.name}
            onChange={(e) => set({ name: e.target.value })}
            disabled={update.isPending}
            maxLength={120}
            aria-invalid={nameError ? true : undefined}
            aria-describedby={nameError ? `${nameId}-error` : undefined}
          />
          {nameError && (
            <span id={`${nameId}-error`} className="auth-error" role="alert">
              {nameError}
            </span>
          )}
        </div>
        <div className="field ag-field">
          <label className="field__label" htmlFor={rutId}>
            RUT
          </label>
          <input
            id={rutId}
            className="input mono"
            value={draft.rut}
            onChange={(e) => set({ rut: e.target.value })}
            disabled={update.isPending}
            maxLength={20}
            inputMode="text"
            autoComplete="off"
            placeholder="12.345.678-5"
            aria-invalid={rutError ? true : undefined}
            aria-describedby={rutError ? `${rutId}-error ${rutHintId}` : rutHintId}
          />
          {rutError && (
            <span id={`${rutId}-error`} className="auth-error" role="alert">
              {rutError}
            </span>
          )}
          <span id={rutHintId} className="field__hint">
            Se valida el dígito verificador antes de guardar.
          </span>
        </div>
      </form>
    </Panel>
  );
}
